// The Threads feed's read/refresh wiring: ThreadsState (what is shown), the
// ThreadReadController (reads as operations) and the SnapshotSequencer (serialized
// canonical refreshes) connected the ONE way the app uses them — so the composed
// schedules (a read settling while another is pending, a snapshot answered between
// them, a refresh after paging) are tested on the real wiring, not on the parts.

import type { ThreadCounts, ThreadStateEvent, ThreadSummary } from "$lib/core/client.js";
import type { ThreadsState } from "$lib/core/state.svelte.js";
import { SnapshotSequencer, ThreadReadController, isUnsupportedByRelay } from "./thread-sync.js";

export interface ThreadSnapshot {
  workspaceId: string;
  threads: ThreadSummary[];
  counts: ThreadCounts;
  // How many rows were asked for (the window), to tell a full window from the end.
  requested: number;
}

// The relay calls the feed makes, as the client exposes them.
export interface ThreadFeedTransport {
  threadState(
    workspaceId: string,
    opts: { limit: number },
  ): Promise<{ threads: ThreadSummary[]; counts: ThreadCounts }>;
  fetchThreads(workspaceId: string, opts: { limit: number }): Promise<{ threads: ThreadSummary[] }>;
  threadCounts(workspaceId: string): Promise<ThreadCounts>;
  markThreadRead(workspaceId: string, rootId: string, viewedAt: number): Promise<unknown>;
}

export interface ThreadFeedSync {
  // Coalesced refresh of the canonical snapshot (every live event lands here).
  refresh(): void;
  // Refresh now; resolves when the snapshot reflecting this call has applied or failed.
  run(): Promise<void>;
  // "Load older threads": widen the window by one page and refresh through the same
  // serialized request as every refresh.
  loadMore(): Promise<void>;
  read(rootId: string, viewedAt: number): void;
  // thread_read_changed from another device: patch the one row, or refresh.
  readChanged(e: ThreadStateEvent): void;
  // Re-send every unconfirmed read of the open workspace with its captured cursor.
  flushReads(): void;
  // Workspace switched: whatever is in flight applies nothing.
  reset(): void;
}

// What a read's send resolves with: the relay's answer plus the moment it was sent.
interface SentRead {
  answer: unknown;
  workspaceId: string;
  // sequencer.fetchCount when the read went out.
  fetchNo: number;
}

// The mark_thread_read answer (ThreadStateEvent) for THIS root, read to zero, with the
// viewer's canonical totals. Anything else (an older relay, a reply that landed after
// the cursor) falls back to the snapshot refresh.
function readToZero(answer: unknown, rootId: string): ThreadCounts | null {
  if (typeof answer !== "object" || answer === null) return null;
  const a: {
    rootId?: unknown;
    unread_replies?: unknown;
    unread_mentions?: unknown;
    counts?: unknown;
  } = answer;
  if (a.rootId !== rootId || a.unread_replies !== 0 || a.unread_mentions !== 0) return null;
  const c = a.counts;
  if (typeof c !== "object" || c === null) return null;
  const {
    totalUnreadThreads,
    totalUnreadMentions,
  }: { totalUnreadThreads?: unknown; totalUnreadMentions?: unknown } = c;
  if (typeof totalUnreadThreads !== "number" || typeof totalUnreadMentions !== "number")
    return null;
  return { totalUnreadThreads, totalUnreadMentions };
}

export function createThreadFeedSync(deps: {
  state: ThreadsState;
  transport: ThreadFeedTransport;
  currentWorkspaceId(): string | null;
  onLoadMoreError?(): void;
}): ThreadFeedSync {
  const { state, transport } = deps;

  async function fetchSnapshot(): Promise<ThreadSnapshot> {
    const workspaceId = deps.currentWorkspaceId();
    if (!workspaceId) throw new Error("no workspace open");
    const requested = state.windowSize;
    try {
      const s = await transport.threadState(workspaceId, { limit: requested });
      return { workspaceId, threads: s.threads, counts: s.counts, requested };
    } catch (err) {
      // A relay older than thread_state: two reads instead of one snapshot — the best
      // that relay can give, still applied in request order.
      if (!isUnsupportedByRelay(err)) throw err;
      const [{ threads }, c] = await Promise.all([
        transport.fetchThreads(workspaceId, { limit: requested }),
        transport.threadCounts(workspaceId),
      ]);
      return {
        workspaceId,
        threads,
        counts: {
          totalUnreadThreads: c.totalUnreadThreads,
          totalUnreadMentions: c.totalUnreadMentions,
        },
        requested,
      };
    }
  }

  const sequencer = new SnapshotSequencer<ThreadSnapshot>({
    fetch: fetchSnapshot,
    apply: (s, fetchNo) => {
      if (deps.currentWorkspaceId() === s.workspaceId) state.applySnapshot(s, s.requested, fetchNo);
    },
    // A missed refresh keeps the last snapshot and shows a retry; the next event or
    // reconnect asks again.
    onError: () => {
      state.loadError = true;
    },
  });

  const reads = new ThreadReadController<SentRead>({
    send: (workspaceId, rootId, viewedAt) => {
      const fetchNo = sequencer.fetchCount;
      return transport
        .markThreadRead(workspaceId, rootId, viewedAt)
        .then((answer) => ({ answer, workspaceId, fetchNo }));
    },
    onLocalRead: (rootId, generation) => state.readLocally(rootId, generation),
    // Lifted by a snapshot requested after this answer: one requested earlier predates
    // the commit and would show the read undone for a round trip.
    onSettled: (rootId, generation) =>
      state.settleOverlay(rootId, generation, sequencer.fetchCount),
    refresh: () => sequencer.schedule(),
    // A read confirmed at zero patches the totals from its own answer instead of
    // re-asking for all 50 threads (CYBORG-1192). Only when nothing could make those
    // totals stale: no other read unconfirmed, no snapshot requested since it was sent.
    confirmed: (rootId, _generation, sent, othersPending) => {
      if (othersPending || sent.fetchNo !== sequencer.fetchCount) return false;
      if (deps.currentWorkspaceId() !== sent.workspaceId) return false;
      const counts = readToZero(sent.answer, rootId);
      if (!counts) return false;
      state.counts = counts;
      return true;
    },
  });

  return {
    refresh: () => sequencer.schedule(),
    run: () => sequencer.run(),
    async loadMore() {
      if (state.loadingMore || !state.growWindow()) return;
      state.loadingMore = true;
      try {
        await sequencer.run();
        if (state.loadError) deps.onLoadMoreError?.();
      } finally {
        state.loadingMore = false;
      }
    },
    read(rootId, viewedAt) {
      const workspaceId = deps.currentWorkspaceId();
      if (workspaceId) reads.read(workspaceId, rootId, viewedAt);
    },
    readChanged(e) {
      // Safe only when nothing newer can be in flight: no snapshot pending and no read of
      // this tab's own unconfirmed. Otherwise the snapshot stays the one source.
      const counts = e.counts;
      const patched =
        sequencer.idle &&
        reads.size === 0 &&
        state.fetched &&
        e.workspaceId !== undefined &&
        e.workspaceId === deps.currentWorkspaceId() &&
        typeof e.unread_replies === "number" &&
        typeof e.unread_mentions === "number" &&
        typeof counts?.totalUnreadThreads === "number" &&
        typeof counts.totalUnreadMentions === "number" &&
        state.applyThreadRead(
          e.rootId,
          { unreadReplies: e.unread_replies, unreadMentions: e.unread_mentions },
          counts,
        );
      if (!patched) sequencer.schedule();
    },
    flushReads() {
      const workspaceId = deps.currentWorkspaceId();
      if (workspaceId) reads.flush(workspaceId);
    },
    reset: () => sequencer.reset(),
  };
}
